import React, { useMemo } from "react";
import styled from "styled-components";
import { ResponsiveHeatMap } from "@nivo/heatmap";

import {
  Card,
  ElementContainer,
} from "../../components/containers/styledComponents";
import { CompassParagraph } from "../../components/common/styledComponents";
import {
  compassGreyBackground,
  darkGreen,
  greyText,
  lightGreyOutline,
  lightGreys,
} from "../../styles";
import { formatReadable } from "../../utils/formattingUtils";
import {
  HeatmapCell,
  ManagerPillarHeatmapData,
  PillarV2,
} from "../../types/engagementAssessmentV2Types";
import { PILLAR_DISPLAY_ORDER, PILLAR_HEATMAP_COLOR_SCALE } from "./constants";
import { ChartEmptyState } from "./ChartEmptyState";
import { HeatmapLegend } from "./HeatmapLegend";

type PillarHeatmapDatum = {
  x: PillarV2;
  y: number | null;
  count: number;
};

type PillarHeatmapSerie = {
  id: string; // manager id — display name resolved on the left axis
  data: PillarHeatmapDatum[];
};

const ROW_HEIGHT_PX = 40;
const CHART_CHROME_PX = 140;
const MIN_CHART_HEIGHT_PX = 220;

const chartHeightFor = (managerCount: number): number =>
  Math.max(managerCount * ROW_HEIGHT_PX + CHART_CHROME_PX, MIN_CHART_HEIGHT_PX);

const ChartContainer = styled.div<{ $height: number }>`
  width: 100%;
  height: ${({ $height }): number => $height}px;
`;

const TooltipBox = styled.div`
  background: white;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid ${lightGreyOutline};
  font-family: "Apercu";
  font-size: 12px;
  color: ${darkGreen};
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
  min-width: 180px;
`;

const TooltipTitle = styled.div`
  font-family: "ApercuBold";
  font-size: 12px;
  color: ${darkGreen};
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 12px;
`;

const TooltipLabel = styled.span`
  color: ${greyText};
`;

const TooltipValue = styled.span`
  color: ${darkGreen};
  font-family: "ApercuMedium";
`;

const EMPTY_CELL_FILL = lightGreys.KPIBackground;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Nivo's CellComponentProps type is not re-exported at the entry we depend on; the shape is stable and asserted below.
const PillarHeatmapCell = (props: any): JSX.Element => {
  const cell = props.cell as {
    id: string;
    value: number | null;
    x: number;
    y: number;
    width: number;
    height: number;
    color: string;
    borderColor: string;
    opacity: number;
  };
  const borderWidth = (props.borderWidth as number) ?? 1;
  const borderRadius = (props.borderRadius as number) ?? 3;
  const onMouseEnter = props.onMouseEnter?.(cell);
  const onMouseMove = props.onMouseMove?.(cell);
  const onMouseLeave = props.onMouseLeave?.(cell);
  const onClick = props.onClick?.(cell);
  const isEmpty = cell.value === null;
  return (
    <g
      data-testid={`pillar-heatmap-cell.${cell.id}`}
      transform={`translate(${cell.x - cell.width / 2}, ${cell.y - cell.height / 2})`}
      style={{ cursor: "pointer" }}
      opacity={cell.opacity}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <rect
        width={cell.width}
        height={cell.height}
        rx={borderRadius}
        ry={borderRadius}
        fill={isEmpty ? EMPTY_CELL_FILL : cell.color}
        stroke={isEmpty ? compassGreyBackground : cell.borderColor}
        strokeWidth={isEmpty ? Math.max(borderWidth, 1) : borderWidth}
        strokeDasharray={isEmpty ? "4 3" : undefined}
      />
    </g>
  );
};

export const ManagerPillarHeatmap = ({
  data,
  isLoading,
  error,
  onRetry,
}: ManagerPillarHeatmapProps): JSX.Element => {
  const managerNameById = useMemo((): Map<string, string> => {
    const m = new Map<string, string>();
    for (const manager of data.managers) m.set(manager.id, manager.name);
    return m;
  }, [data.managers]);

  const nivoData = useMemo((): PillarHeatmapSerie[] => {
    const cellsByManager = new Map<string, HeatmapCell<PillarV2>[]>();
    for (const cell of data.cells) {
      const list = cellsByManager.get(cell.row);
      if (list) list.push(cell);
      else cellsByManager.set(cell.row, [cell]);
    }
    return data.managers.map((manager) => {
      const managerCells = cellsByManager.get(manager.id) ?? [];
      const cellByCol = new Map<PillarV2, HeatmapCell<PillarV2>>();
      for (const cell of managerCells) cellByCol.set(cell.col, cell);
      return {
        id: manager.id,
        data: PILLAR_DISPLAY_ORDER.map((pillar): PillarHeatmapDatum => {
          const cell = cellByCol.get(pillar);
          const rawValue = cell?.value;
          const y =
            cell === undefined ||
            rawValue === undefined ||
            Number.isNaN(rawValue)
              ? null
              : rawValue;
          return {
            x: pillar,
            y,
            count: cell?.count ?? 0,
          };
        }),
      };
    });
  }, [data]);

  const chartHeight = chartHeightFor(data.managers.length);
  const cardHeight = "500px";

  if (isLoading) {
    return (
      <Card h={cardHeight} isLoading={true}>
        <CompassParagraph fontSize={16} bold={true}>
          Manager × Pillar Heatmap
        </CompassParagraph>
      </Card>
    );
  }

  if (error !== null) {
    return (
      <Card h={cardHeight}>
        <CompassParagraph fontSize={16} bold={true} mb={12}>
          Manager × Pillar Heatmap
        </CompassParagraph>
        <ChartEmptyState
          variant="error"
          entityName="manager × pillar cells"
          onRetry={onRetry}
        />
      </Card>
    );
  }

  const isEmpty = data.managers.length === 0 || data.cells.length === 0;
  if (isEmpty) {
    return (
      <Card h={cardHeight}>
        <CompassParagraph fontSize={16} bold={true} mb={12}>
          Manager × Pillar Heatmap
        </CompassParagraph>
        <ChartEmptyState variant="empty" entityName="manager × pillar cells" />
      </Card>
    );
  }

  return (
    <Card h={cardHeight}>
      <CompassParagraph fontSize={16} bold={true} mb={12}>
        Manager × Pillar Heatmap
      </CompassParagraph>
      <ChartContainer $height={chartHeight}>
        <ResponsiveHeatMap
          data={nivoData}
          margin={{ top: 60, right: 24, bottom: 20, left: 180 }}
          valueFormat={(value: number): string => value.toFixed(2)}
          axisTop={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: -25,
            format: (v: string): string => formatReadable(v),
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: 0,
            format: (v: string): string => managerNameById.get(v) ?? v,
          }}
          colors={{
            type: "quantize",
            steps: 5,
            colors: [...PILLAR_HEATMAP_COLOR_SCALE],
            domain: [0, 1],
          }}
          emptyColor={EMPTY_CELL_FILL}
          borderRadius={3}
          borderWidth={1}
          borderColor={lightGreyOutline}
          enableLabels={false}
          cellComponent={PillarHeatmapCell}
          hoverTarget="cell"
          tooltip={({ cell }): JSX.Element => {
            const managerName =
              managerNameById.get(String(cell.serieId)) ?? String(cell.serieId);
            const datum = cell.data as PillarHeatmapDatum;
            const value = cell.value;
            return (
              <TooltipBox>
                <TooltipTitle>{managerName}</TooltipTitle>
                <TooltipRow>
                  <TooltipLabel>Pillar</TooltipLabel>
                  <TooltipValue>{formatReadable(datum.x)}</TooltipValue>
                </TooltipRow>
                <TooltipRow>
                  <TooltipLabel>Value</TooltipLabel>
                  <TooltipValue>
                    {value === null || value === undefined
                      ? "No data"
                      : Number(value).toFixed(2)}
                  </TooltipValue>
                </TooltipRow>
                <TooltipRow>
                  <TooltipLabel>Engagements</TooltipLabel>
                  <TooltipValue>{datum.count}</TooltipValue>
                </TooltipRow>
              </TooltipBox>
            );
          }}
          theme={{
            text: { fontFamily: "Apercu", fontSize: 11, fill: darkGreen },
            axis: {
              ticks: {
                text: {
                  fontFamily: "Apercu",
                  fontSize: 11,
                  fill: greyText,
                },
              },
            },
            tooltip: { container: { background: "transparent", padding: 0 } },
          }}
          animate={false}
        />
      </ChartContainer>
      <ElementContainer mt={12} jc="flex-start">
        <HeatmapLegend variant="pillar" />
      </ElementContainer>
    </Card>
  );
};

type ManagerPillarHeatmapProps = {
  data: ManagerPillarHeatmapData;
  isLoading: boolean;
  error: string | null;
  onRetry?: () => void;
};
